import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import {
  DailyLogCreateResponse,
  DailyLogDiaryType,
  DailyLogDiaryTypeDataListResponse,
  DailyLogDiaryTypeItem,
  DailyLogEmojiListResponse,
  DailyLogFriendProfilePostListResponse,
  DailyLogMemeListResponse,
  DailyLogMemeType,
  DailyLogMetaDataResponse,
  DailyLogMyPostListResponse,
  DailyLogPost,
  DailyLogPostCreateBody,
  DailyLogPostResponse,
  DailyLogPostUpdateBody,
  DailyLogReactionCreateBody,
  DailyLogReactionResponse,
  DailyLogReactionType,
  DailyLogReactionUpdateBody,
  DailyLogStickerListResponse,
  DailyLogStickerResponse,
  DailyLogTemplateMemeListResponse,
  DailyLogTemplatePaginatedListResponse,
} from 'src/app/interfaces/daily-log'
import { CREATED_DAILY_LOGS_STORAGE_KEY } from 'src/app/pages/daily-log/daily-log-template/daily-log-template.component'
import { environment } from 'src/environments/environment'
import { transformToKebabCase } from 'src/utils/case-styles'
import { isDailyLogContentString } from 'src/utils/daily-log-content'
import { customLocalStorage } from 'src/utils/utils'
import { CustomHttpClient } from '../http/http.service'

export const DailyLogStickerTooltipClosedOnCreateStorageKey = 'daily_log_sticker_tooltip_closed_on_create'
export const DailyLogStickerTooltipClosedOnDetailStorageKey = 'daily_log_sticker_tooltip_closed_on_detail'

@Injectable({
  providedIn: 'root',
})
export class DailyLogService {
  constructor(private http: CustomHttpClient) {
    this.fetchDailyLogDiaryTypeDataList().subscribe()
  }

  private _memoCommentClickSubject = new Subject<void>()

  private _diaryTypeList: DailyLogDiaryTypeItem[] = []

  diaryTypeList$ = new BehaviorSubject<DailyLogDiaryTypeItem[]>([])

  get diaryTypeList(): DailyLogDiaryTypeItem[] {
    return this._diaryTypeList
  }

  getMemoCommentClickSubject(): Observable<void> {
    return this._memoCommentClickSubject.asObservable()
  }

  onClickMemoComment(): void {
    this._memoCommentClickSubject.next()
  }

  fetchDailyLogMetaData(): Observable<DailyLogMetaDataResponse> {
    return this.http.get<DailyLogMetaDataResponse>(`${environment.apiUrl}/v1/log/posts/meta`)
  }

  /** 로그 쓰기 */
  createDailyLogPost(body: DailyLogPostCreateBody): Observable<DailyLogCreateResponse> {
    const formData = new FormData()
    if (body.image !== undefined) {
      formData.append('image', body.image)
    }
    if (body.nickname) {
      formData.append('nickname', body.nickname)
    }
    if (body.content) {
      formData.append('content', body.content)
    }
    if (body.postedAt !== undefined) {
      formData.append('posted_at', body.postedAt)
    }
    if (Array.isArray(body.friendGroupIds) && body.friendGroupIds.length > 0) {
      formData.append('friend_group_ids', JSON.stringify(body.friendGroupIds))
    }
    if (body.staticGroups) {
      formData.append('static_groups', JSON.stringify(body.staticGroups))
    }
    if (body.deletionReservedAt) {
      formData.append('deletion_reserved_at', body.deletionReservedAt)
    }
    if (body.receiverIds) {
      formData.append('receiver_ids', JSON.stringify(body.receiverIds))
    }
    if (body.title) {
      formData.append('title', body.title)
    }
    if (body.link) {
      formData.append('link', body.link)
    }
    if (body.type) {
      formData.append('type', body.type)
    }
    if (body.keyword) {
      formData.append('keyword', body.keyword)
    }
    if (body.bgColor) {
      formData.append('bg_color', body.bgColor)
    }
    if (body.bgImageId) {
      formData.append('bg_image_id', `${body.bgImageId}`)
    }
    if (body.templateId) {
      formData.append('template_id', `${body.templateId}`)
    }
    if (body.userIdentifier) {
      formData.append('user_identifier', `${body.userIdentifier}`)
    }
    return this.http.post<DailyLogCreateResponse>(`${environment.apiUrl}/v1/log/posts`, formData)
  }

  /** 로그 상세 가져오기 */
  fetchDailyLogPostDetail(id: number): Observable<DailyLogPostResponse> {
    return this.http.get<DailyLogPostResponse>(`${environment.apiUrl}/v1/log/posts/${id}`).pipe(
      map((res: DailyLogPostResponse) => {
        // style의 key값을 snake_case로 변경(RN에 맞춰져있어서 서버에서 camelCase로 옴)
        if (isDailyLogContentString(res.data.content)) {
          const parsedContent = JSON.parse(res.data.content)
          if (parsedContent.globalStyle.fontSize) {
            parsedContent.globalStyle.fontSize = `${parsedContent.globalStyle.fontSize / 16}rem`
          }
          parsedContent.globalStyle = transformToKebabCase(parsedContent.globalStyle)
          parsedContent.texts = parsedContent.texts.map((text: any) => {
            text.style = transformToKebabCase(text.style)
            return text
          })
          res.data.content = JSON.stringify(parsedContent)
        }
        return res
      })
    )
  }

  /* 로그 밈 리스트 가져오기*/
  fetchDailyLogMemeList(
    cursor: string | null,
    pageSize: number = 40,
    type?: DailyLogMemeType
  ): Observable<DailyLogMemeListResponse> {
    return this.http.get<DailyLogMemeListResponse>(`${environment.apiUrl}/v1/reaction/memes`, {
      queryParams: { cursor, pageSize, type: type ?? null },
    })
  }

  /* 로그 템플릿 밈 리스트 가져오기*/
  fetchDailyLogTemplateMemeList(
    templateId: number,
    cursor: string | null,
    pageSize: number = 40
  ): Observable<DailyLogTemplateMemeListResponse> {
    return this.http.get<DailyLogTemplateMemeListResponse>(`${environment.apiUrl}/v1/reaction/memes`, {
      queryParams: { cursor, pageSize, logTemplateId: templateId },
    })
  }

  /** 이모지 목록 가져오기 */
  fetchDailyLogEmojiList(cursor: string | null, pageSize: number = 40): Observable<DailyLogEmojiListResponse> {
    return this.http.get<DailyLogEmojiListResponse>(`${environment.apiUrl}/v1/reaction/emojis`, {
      queryParams: { cursor, pageSize },
    })
  }

  /* 로그 리액션 삭제하기 */
  deleteDailyLogReaction(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/v1/log/post-reactions/${id}`)
  }

  /* 로그 리액션 수정하기 */
  updateDailyLogReaction(id: number, body: DailyLogReactionUpdateBody): Observable<DailyLogReactionResponse> {
    return this.http.patch<DailyLogReactionResponse>(`${environment.apiUrl}/v1/log/post-reactions/${id}`, body)
  }

  /* 로그 리액션 하기 */
  postDailyLogReaction(id: number, body: DailyLogReactionCreateBody): Observable<DailyLogReactionResponse> {
    return this.http.post<DailyLogReactionResponse>(`${environment.apiUrl}/v1/log/posts/${id}/reactions`, body)
  }

  /** 로그 글 타입 데이터 가져오기 */
  fetchDailyLogDiaryTypeDataList(): Observable<DailyLogDiaryTypeDataListResponse> {
    return this.http.get<DailyLogDiaryTypeDataListResponse>(`${environment.apiUrl}/v1/log/post-types`).pipe(
      tap((res) => {
        this._diaryTypeList = res.data?.postTypes ?? []
        this.diaryTypeList$.next(this._diaryTypeList)
      })
    )
  }

  getDailyLogDiaryTypeData = (type: DailyLogDiaryType) => {
    const defaultData: DailyLogDiaryTypeItem = {
      type: DailyLogDiaryType.ESSAY,
      icon: {
        emoji: '✍️',
        color: '#A8FFBB',
      },
      title: '긴 글 쓰기',
      description: '일상 또는 생각들을 자유롭게\n작성해 보세요',
      gradientColors: ['rgba(137, 255, 239, 0.60)', 'rgba(137, 255, 239, 0.15)'],
      backgroundColor: '#F3FFE8',
    }
    return this.diaryTypeList.find((item) => item.type === type) ?? defaultData
  }

  /** template으로 로그 생성 */
  createDailyLogByTemplate(
    templateId: number,
    anonymous?: { userIdentifier: string; nickname: string }
  ): Observable<DailyLogPostResponse> {
    const body: any = {
      templateId,
      staticGroups: JSON.stringify(['ANYONE']),
    }
    if (anonymous) {
      const { userIdentifier, nickname } = anonymous
      ;(body.userIdentifier = userIdentifier), (body.nickname = nickname)
    }
    return this.http.post<DailyLogPostResponse>(`${environment.apiUrl}/v1/log/posts`, body)
  }

  /** template 목록 가져오기 */
  fetchDailyLogTemplateList(
    cursor: string | null,
    pageSize: number = 20
  ): Observable<DailyLogTemplatePaginatedListResponse> {
    return this.http.get<DailyLogTemplatePaginatedListResponse>(`${environment.apiUrl}/v1/log/post-templates`, {
      queryParams: { cursor, pageSize },
    })
  }

  fetchMyDailyLogList(cursor: string | null, pageSize: number = 20): Observable<DailyLogMyPostListResponse> {
    return this.http.get<DailyLogMyPostListResponse>(`${environment.apiUrl}/v1/log/posts/mine`, {
      queryParams: { cursor, pageSize },
    })
  }

  //** 친구 프로필 일기 목록 가져오기 */
  fetchFriendProfileLogList(
    id: number,
    cursor: string | null,
    pageSize: number = 20
  ): Observable<DailyLogFriendProfilePostListResponse> {
    return this.http.get<DailyLogFriendProfilePostListResponse>(`${environment.apiUrl}/v1/log/posts/users/${id}`, {
      queryParams: { cursor, pageSize },
    })
  }

  /** 로그인시, 비회원으로 만든 로그들 연결시키기 */
  connectAnonymousDailyLog(uuid: string): void {
    const createdLogs = JSON.parse(customLocalStorage.getItem(CREATED_DAILY_LOGS_STORAGE_KEY) ?? '[]')
    if (createdLogs.length === 0) return
    this.http.post<void>(`${environment.apiUrl}/v1/log/posts/connect`, { userIdentifier: uuid }).subscribe()
  }

  getCreatedLogFromLocalStorage(id: number): DailyLogPost | undefined {
    const list = this.getCreatedLogListFromLocalStorage()
    return list.find((item) => item.id === id)
  }

  getCreatedLogListFromLocalStorage(): DailyLogPost[] {
    const createdDailyLogs: DailyLogPost[] = JSON.parse(
      customLocalStorage.getItem(CREATED_DAILY_LOGS_STORAGE_KEY) ?? '[]'
    )
    return createdDailyLogs
  }

  //** 로그 수정하기 (웹에서는 isReactionPrivate만 쓰임) */
  updateDailyLogPost(id: number, body: DailyLogPostUpdateBody): Observable<DailyLogPostResponse> {
    const formData = new FormData()
    if (body.image !== undefined) {
      formData.append('image', body.image)
    }
    if (body.content) {
      formData.append('content', body.content)
    }
    if (body.link) {
      formData.append('link', body.link)
    }
    if (body.type) {
      formData.append('type', body.type)
    }
    if (body.keyword) {
      formData.append('keyword', body.keyword)
    }
    if (body.postedAt) {
      formData.append('posted_at', body.postedAt)
    }
    if (body.bgColor) {
      formData.append('bg_color', body.bgColor)
    }
    if (body.bgImageId) {
      formData.append('bg_image_id', `${body.bgImageId}`)
    }
    if (body.bgImageId) {
      formData.append('bg_image_id', `${body.bgImageId}`)
    }
    if (body.isReactionPrivate) {
      formData.append('is_reaction_private', `${body.isReactionPrivate}`)
    }
    return this.http.patch<DailyLogPostResponse>(`${environment.apiUrl}/v1/log/posts/${id}`, formData)
  }

  /* 내가 등록한 스티커 목록 */
  getDailyLogMyStickerList(
    cursor: string | null,
    uuid?: string,
    pageSize: number = 20
  ): Observable<DailyLogStickerListResponse> {
    const queryParams: { [key: string]: string | number | null } = { cursor, pageSize }
    if (uuid !== undefined) {
      queryParams.userIdentifier = uuid
    }
    return this.http
      .get<DailyLogStickerListResponse>(`${environment.apiUrl}/v1/reaction/stickers/mine`, {
        queryParams,
      })
      .pipe(
        map((res) => {
          res.data.stickers = res.data.stickers.map((sticker) => ({ ...sticker, type: DailyLogReactionType.STICKER }))
          return res
        })
      )
  }
  /* 로그 스티커 등록하기 */
  registerDailyLogSticker(image: File, isApiUsed: boolean, uuid?: string): Observable<DailyLogStickerResponse> {
    const formData = new FormData()
    if (image !== undefined) {
      formData.append('image', image)
    }
    if (isApiUsed !== undefined) {
      formData.append('is_api_used', JSON.stringify(isApiUsed))
    }
    if (uuid !== undefined) {
      formData.append('user_identifier', uuid)
    }
    return this.http.post<DailyLogStickerResponse>(`${environment.apiUrl}/v1/reaction/stickers`, formData)
  }
}
